import { useFormik } from "formik";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "src/components/buttons/Button";
import useNotification from "src/hooks/useNotification";
import { CheckBoxIcon } from "src/icons/CheckboxIcon";
import { initialize } from "src/redux/actions/app";
import { object, string } from "yup";
import Input from "../../components/inputs/Input";
import { setTokenInLocalStorage } from "../../helpers/helpers";
import PublicLayout from "./PublicLayout";
import { useLogin } from "./api-client";

const FORM_FIELDS = {
  EMAIL: "email",
  PASSWORD: "password",
  REMEMBER_ME: "rememberMe",
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { errorNotification } = useNotification();
  const { execute, isLoading, isSuccess, data, error } = useLogin();
  const { values, errors, handleSubmit, setFieldValue, touched } = useFormik({
    initialValues: {
      [FORM_FIELDS.EMAIL]: "",
      [FORM_FIELDS.PASSWORD]: "",
      [FORM_FIELDS.REMEMBER_ME]: false,
    },
    validationSchema: object({
      [FORM_FIELDS.EMAIL]: string()
        .email("Please enter valid email.")
        .required("This is a Required field."),
      [FORM_FIELDS.PASSWORD]: string().required("This is a Required field."),
    }),
    onSubmit: ({ email, password }) => {
      execute(email, password);
    },
  });
  useEffect(() => {
    if (isSuccess) {
      setTokenInLocalStorage(data.data.token);
      dispatch(initialize());
      // navigate(data?.data?.role === 1 ? "/" : "/select-app");
      navigate("/");
    }
  }, [data?.data?.role, data?.data?.token, dispatch, isSuccess, navigate]);

  useEffect(() => {
    if (error) {
      const errorStatus = error?.cause?.status;
      if ([403, 404].includes(errorStatus)) {
        errorNotification("Invalid Email/Password.");
      } else {
        errorNotification();
      }
    }
  }, [error, errorNotification]);

  return (
    <PublicLayout
      leftSection={{
        heading: "Welcome Back! Your CRM Awaits",
        features: [
          {
            text: "Enter your registered email and password to access your account.",
          },
          {
            text: "Enjoy a personalized experience and stay updated with our latest  offers and services!",
          },
        ],
      }}
      rightSection={{
        heading: "Sign In",
        subHeading: "Please enter your email and password to continue",
      }}
    >
      <div className="flex flex-col">
        <form
          onSubmit={handleSubmit}
          className="flex flex-col justify-start"
          noValidate
        >
          <Input
            type="email"
            label="Enter Email"
            placeholder="Enter Email"
            name={FORM_FIELDS.EMAIL}
            value={values[FORM_FIELDS.EMAIL] as string}
            error={
              touched[FORM_FIELDS.EMAIL] ? errors[FORM_FIELDS.EMAIL] : undefined
            }
            onChange={(e) => setFieldValue(FORM_FIELDS.EMAIL, e.target.value)}
            disabled={isLoading}
            required
          />
          <Input
            type="password"
            label="Enter Password"
            placeholder="Enter Password"
            name={FORM_FIELDS.PASSWORD}
            value={values[FORM_FIELDS.PASSWORD] as string}
            error={
              touched[FORM_FIELDS.PASSWORD]
                ? errors[FORM_FIELDS.PASSWORD]
                : undefined
            }
            onChange={(e) =>
              setFieldValue(FORM_FIELDS.PASSWORD, e.target.value)
            }
            disabled={isLoading}
            required
            className="mt-6"
          />
          <div className="flex gap-3 justify-between text-sm text-right mt-6">
            <div className="flex gap-2 items-center">
              <CheckBoxIcon
                checked={values[FORM_FIELDS.REMEMBER_ME] as boolean}
                onClick={(value) =>
                  setFieldValue(FORM_FIELDS.REMEMBER_ME, value)
                }
              />
              <span>Remember Me</span>
            </div>
            <span
              onClick={() => navigate("/forgot-password")}
              className="font-semibold text-primary hover:text-primary-800 cursor-pointer"
            >
              Forgot password?
            </span>
          </div>

          <Button
            className={"mt-[50px] w-[70%]"}
            type="submit"
            text={isLoading ? "Loading..." : "Login"}
            primary
          />
        </form>

        <p className="mt-[15px] text-left text-sm text-gray-500 gap-2">
          Don't have an account ?{" "}
          <span
            onClick={() => navigate("/register")}
            className="font-semibold leading-6 text-primary hover:text-primary-800 cursor-pointer"
          >
            Sign Up
          </span>
        </p>
      </div>
    </PublicLayout>
  );
};

export default Login;
